import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Typography,
  Popover,
} from 'antd';
import { ExclamationOutlined, MoonOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';

import './TimeCard.less';
import Scroller from './Scroller';

import ListCard from '../../common/ListCard';
import colors from '../../constants/Colors';
import { safeExtractFromObject } from '../../helpers/helpers';

export default function TimeCard({
  day,
  onClick,
  roundingType,
  roundingInterval,
  roundingSetting,
  onEditClicked,
  isApprovals,
  userId,
  hoursBreakdownMap,
  showOtBreakdown,
  hasPaidFlag,
  onCardLinkClick,
}) {
  const {
    dayTasks = [],
    dayHistory = [],
  } = day;

  const { enableTimecardSubmission = false } = useSelector(
    (state) => safeExtractFromObject(state, 'settings.company.settings'),
  );

  const hasViolation = dayTasks.some((task) => task.geofenceViolation);
  const hasAutoClockedOut = dayTasks.some((task) => task.isAutoClockOut);
  const hasOutsideWorkingHours = dayTasks.some((task) => task.outsideWorkingHours);
  const isAutoRounded = dayTasks.some((task) => task.isAutoRounded);
  const hasNightShift = dayTasks.some((task) => task.isNightShift);
  const dayHoursBreakdown = useMemo(() => (
    dayTasks.reduce((acc, task) => {
      const { id } = task;
      const {
        regularDuration = 0,
        otDuration = 0,
        dtDuration = 0,
        pto = 0,
      } = hoursBreakdownMap?.[userId]?.[id] || {};
      acc.regularTime += regularDuration;
      acc.overtime += otDuration;
      acc.doubleOT += dtDuration;
      acc.pto += pto;
      return acc;
    }, {
      regularTime: 0,
      overtime: 0,
      doubleOT: 0,
      pto: 0,
    })
  ), [hoursBreakdownMap, dayTasks]);

  const latestTimeCardEvent = useMemo(() => (
    dayHistory[dayHistory.length - 1] ?? {}
  ), [dayHistory]);

  const isRecentlyRejected = useMemo(() => (
    latestTimeCardEvent?.type === 'rejected'
  ), [latestTimeCardEvent]);

  const popoverContent = useMemo(() => {
    if (
      !isRecentlyRejected
      && !hasViolation
      && !hasOutsideWorkingHours
      && !hasAutoClockedOut
      && !isAutoRounded
      && !hasNightShift
    ) return null;
    const { timestamp = 0, note } = latestTimeCardEvent;
    const date = DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_MED);
    const noteMsg = note ? ` with a note: ${note}` : '.';

    return (
      <div style={{ width: 300 }}>
        {isRecentlyRejected && (
          <div style={{ marginBottom: hasViolation && 20 }}>
            <Row>
              <b>{`Timecard Rejected - ${date}`}</b>
            </Row>
            <Row>
              {`This Timecard was rejected${noteMsg}`}
            </Row>
          </div>
        )}
        {hasViolation && (
          <div>
            <Row>
              <b>Geofence Breach:</b>
            </Row>
            <Row>
              This Timecard has a Geofence Breach.
            </Row>
          </div>
        )}
        {hasAutoClockedOut && (
          <div>
            <Row>
              This Timecard contains automatic clock-out entries.
            </Row>
          </div>
        )}
        {hasOutsideWorkingHours && (
          <div>
            <Row>
              This Timecard is outside working hours
            </Row>
          </div>
        )}
        { !!isAutoRounded && (
          <div>
            <Row>
              This Timecard has been auto-rounded
            </Row>
          </div>
        )}
        {hasNightShift && (
          <div>
            <Row>
              <b>Night Shift:</b>
            </Row>
            <Row>
              This Timecard contains night shift entries
            </Row>
          </div>
        )}
      </div>
    );
  }, [
    hasViolation,
    isRecentlyRejected,
    hasOutsideWorkingHours,
    latestTimeCardEvent,
    hasAutoClockedOut,
    isAutoRounded,
    hasNightShift,
  ]);

  const timeCardIndicator = useMemo(() => {
    const hasOtherIndicators = hasViolation
      || isRecentlyRejected
      || hasOutsideWorkingHours
      || hasAutoClockedOut
      || isAutoRounded;

    if (hasOtherIndicators) {
      return {
        icon: ExclamationOutlined,
        top: 3,
        backgroundColor: colors.ONTRACCR_RED,
      };
    }

    if (hasNightShift) {
      return {
        icon: MoonOutlined,
        top: 3,
        backgroundColor: colors.ONTRACCR_BLACK,
      };
    }

    return null;
  }, [
    hasViolation,
    isRecentlyRejected,
    hasOutsideWorkingHours,
    hasAutoClockedOut,
    isAutoRounded,
    hasNightShift,
  ]);

  let listCardHeight = '16.88rem';

  if (isApprovals || showOtBreakdown) {
    listCardHeight = '19.5rem';

    if (showOtBreakdown && hasPaidFlag) {
      listCardHeight = '21rem';
    }
  }

  const borderStyle = day.submittable && enableTimecardSubmission
    ? { borderColor: colors.ONTRACCR_RED, borderWidth: 2 }
    : { borderColor: colors.ONTRACCR_BLACK, borderWidth: 1 };

  return (
    <ListCard
      style={{
        width: '11rem',
        height: listCardHeight,
        ...borderStyle,
      }}
      bodyStyle={{ height: '100%' }}
      onClick={() => onClick({
        day,
      })}
    >
      <Row
        style={{
          height: '30px',
          minWidth: '100%',
          paddingBottom: 0,
          paddingTop: 6,
          overflowWrap: 'anywhere',
        }}
        type="flex"
        justify="space-between"
      >
        <Col span={18}>
          <Row style={{ width: '100%', lineHeight: '9px' }}>
            <Typography.Text
              ellipsis
              style={{
                width: '100%',
                fontFamily: 'raleway-medium',
                fontSize: 10,
                color: colors.ONTRACCR_BLACK,
              }}
            >
              {`${day.weekday},`}
            </Typography.Text>
          </Row>
          <Row style={{ width: '100%', lineHeight: '9px' }}>
            <Typography.Text
              ellipsis
              style={{
                width: '100%',
                fontFamily: 'raleway-medium',
                fontSize: 10,
                color: colors.ONTRACCR_BLACK,
              }}
            >
              {`${day.month} ${day.dayOfMonth}`}
            </Typography.Text>
          </Row>
        </Col>
        <Col span={6}>
          <Typography.Text
            ellipsis
            style={{
              width: '100%',
              fontFamily: 'raleway-medium',
              fontSize: 10,
              color: colors.ONTRACCR_BLACK,
              textAlign: 'right',
            }}
          >
            {day.year}
          </Typography.Text>
        </Col>
      </Row>

      <Row
        style={{
          minHeight: 100,
          alignContent: 'center',
        }}
        type="flex"
      >
        <Scroller
          day={day}
          dayHoursBreakdown={dayHoursBreakdown}
          onEditClicked={onEditClicked}
          roundingType={roundingType}
          roundingInterval={roundingInterval}
          roundingSetting={roundingSetting}
          isApprovals={isApprovals}
          userId={userId}
          showOtBreakdown={showOtBreakdown}
          hasPaidFlag={hasPaidFlag}
          onCardLinkClick={onCardLinkClick}
        />
      </Row>
      {timeCardIndicator && (
        <Popover
          trigger="hover"
          content={popoverContent}
          placement="topRight"
        >
          <timeCardIndicator.icon
            style={{
              position: 'absolute',
              right: 3,
              top: timeCardIndicator.top,
              color: 'white',
              backgroundColor: timeCardIndicator.backgroundColor,
              padding: 1,
              height: 12,
              width: 12,
              borderRadius: 6,
              fontSize: 10,
            }}
          />
        </Popover>
      )}
    </ListCard>
  );
}

/* eslint-disable react/forbid-prop-types */
TimeCard.propTypes = {
  day: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  roundingType: PropTypes.string,
  roundingInterval: PropTypes.number.isRequired,
  roundingSetting: PropTypes.string,
  onEditClicked: PropTypes.func.isRequired,
  isApprovals: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  hoursBreakdownMap: PropTypes.object,
  showOtBreakdown: PropTypes.bool,
  hasPaidFlag: PropTypes.bool,
  onCardLinkClick: PropTypes.func,
};

TimeCard.defaultProps = {
  roundingType: null,
  isApprovals: false,
  roundingSetting: undefined,
  hoursBreakdownMap: {},
  showOtBreakdown: false,
  hasPaidFlag: false,
  onCardLinkClick: undefined,
};
